import { Place, fmtSCU } from '@itc-mod/supply/shared';
import { z } from 'zod';
import { Line, LineInput, Pickup, PickupInput, Settlement, type Donation } from './schemas.ts';

// Incoming (plan 2C Assumption 6, R12): pledges with pickup logistics, and
// expected deliveries: Sierra's Shipment, Purchase and Transfer, which are stock
// coming in that isn't a gift. A delivery earns no credit, and is stocked through
// the same handler as a donation when it's received.

export const DeliveryKind = z.enum(['shipment', 'purchase', 'transfer']);
export type DeliveryKind = z.infer<typeof DeliveryKind>;

export const DELIVERY_LABEL: Readonly<Record<DeliveryKind, string>> = { shipment: 'Shipment', purchase: 'Purchase', transfer: 'Transfer' };

export const DeliveryStatus = z.enum(['expected', 'received', 'cancelled']);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

export const Delivery = z.object({
  id: z.string(),
  kind: DeliveryKind,
  from: z.string(),
  lines: z.array(Line),
  request: z.string().nullable(),
  place: Place.nullable(),
  pickup: Pickup.nullable(),
  status: DeliveryStatus,
  notes: z.string(),
  createdBy: z.string(),
  receivedBy: z.string().nullable(),
  receivedAt: z.iso.datetime().nullable(),
  settlement: Settlement,
  created: z.iso.datetime(),
  version: z.number().int(),
});
export type Delivery = z.infer<typeof Delivery>;

const DeliveryFields = z.object({
  kind: DeliveryKind,
  from: z.string().trim().min(1).max(100),
  lines: z.array(LineInput).min(1).max(30),
  request: z.string().max(15).nullable().default(null),
  place: Place.nullable().default(null),
  pickup: PickupInput.nullable().default(null),
  notes: z.string().trim().max(1000).default(''),
});

export const DeliveryInput = DeliveryFields;
export type DeliveryInput = z.input<typeof DeliveryInput>;

export const DeliveryUpdate = DeliveryFields.omit({ kind: true }).extend({ version: z.number().int().min(1) });
export type DeliveryUpdate = z.input<typeof DeliveryUpdate>;

/** Receiving: where it went, if not where it was expected. */
export const ReceiveInput = z.object({ version: z.number().int().min(1), place: Place.nullable().default(null) });

/** R8: a received delivery's reason. "Received: Purchase from Hurston Dynamics". */
export function receivedReason(delivery: Pick<Delivery, 'kind' | 'from'>): string {
  return `Received: ${DELIVERY_LABEL[delivery.kind]} from ${delivery.from}`.slice(0, 200);
}

/** One card on the Incoming tab: an open pledge with logistics, or an open delivery. */
export type IncomingEntry = { kind: 'donation'; donation: Donation } | { kind: 'delivery'; delivery: Delivery };

function pickupAtOf(entry: IncomingEntry): string | null {
  return entry.kind === 'donation' ? (entry.donation.pickup?.at ?? null) : (entry.delivery.pickup?.at ?? null);
}

function createdOf(entry: IncomingEntry): string {
  return entry.kind === 'donation' ? entry.donation.created : entry.delivery.created;
}

/** R12: by pickup time with none last, then newest first. */
export function incomingOrder(a: IncomingEntry, b: IncomingEntry): number {
  const at = (entry: IncomingEntry) => {
    const value = pickupAtOf(entry);
    return value === null ? Number.POSITIVE_INFINITY : Date.parse(value);
  };
  return at(a) - at(b) || Date.parse(createdOf(b)) - Date.parse(createdOf(a));
}

/**
 * R12 (`capacityNote`, line 2005): with a supply ship hauling, trips = ceil(load ÷
 * capacity). "Fits in Ironclad (40 of 96 SCU)" or "Needs 2 trips in Cutter (60 SCU,
 * holds 46)". Nothing with no load, no capacity, or a ship's name typed in. Loads
 * and capacities are in thousandths of an SCU.
 */
export function capacityNote(load: number, ship: { name: string; cargoCapacity: number | null } | null): string {
  if (load <= 0 || ship === null || ship.cargoCapacity === null || ship.cargoCapacity <= 0) return '';
  const trips = Math.ceil(load / ship.cargoCapacity);
  return trips <= 1 ? `Fits in ${ship.name} (${fmtSCU(load)} of ${fmtSCU(ship.cargoCapacity)} SCU)` : `Needs ${String(trips)} trips in ${ship.name} (${fmtSCU(load)} SCU, holds ${fmtSCU(ship.cargoCapacity)})`;
}
